import type { ChangeEvent } from 'react';
import { useEffect, useState } from 'react';

import { useSelector } from 'react-redux';

import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';
import { useAppDispatch } from '@/hooks/redux-hooks';
import { createApiKey, resetGenerated } from '@/slices/apiKeyListSlice';
import { downloadAsCsv } from '@/utils/downloadAsCsv';

import { createNewKeyName } from './createNewApiKeyName';

import type { RootState } from 'store';

const useGenerationBody = () => {
  const { formatDateString } = useDateText();
  const [apiKeyName, setApiKeyName] = useState('');
  const [error, setError] = useState('');
  const dispatch = useAppDispatch();
  const listApiKeyState = useSelector((state: RootState) => state.listApiKey);
  const loading = listApiKeyState.status === 'loading';
  const generated = listApiKeyState.generated;
  const handleClose = (setOpen: (state: boolean) => void) => {
    dispatch(resetGenerated());
    setOpen(false);
  };
  useEffect(() => {
    if (!apiKeyName)
      setApiKeyName(`my_apikey_${formatDateString(new Date().toJSON(), DateTextVariants.FULL)}`);
  }, [formatDateString]);

  const exportToCsv = () => {
    const header = 'Name,Key';
    const apiKey = listApiKeyState.apiKeyOutputs.at(-1);
    const name = apiKey?.label;
    const token = apiKey?.prefixedRawApiKey ?? '';
    const keyRow = `${name},${token}`;
    const values = [header, keyRow];
    downloadAsCsv(values);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (error) setError('');
    setApiKeyName(event.target.value);
  };
  const sendCreateApiKey = (keyName: string) => {
    dispatch(createApiKey(keyName)).then(response => {
      if (response.meta.requestStatus === 'rejected') {
        if (response.payload.errors[0].includes('already exists')) {
          const newKeyName = createNewKeyName(keyName, listApiKeyState.apiKeyOutputs);
          sendCreateApiKey(newKeyName);
          return;
        }
        setError(response.payload.errors?.[0] ?? 'An unknown error occurred');
      }
    });
  };
  const onGenerate = () => {
    sendCreateApiKey(apiKeyName);
  };

  return {
    handleChange,
    apiKeyName,
    onGenerate,
    listApiKeyState,
    exportToCsv,
    handleClose,
    generated,
    error,
    loading
  };
};

export default useGenerationBody;
